import styled, { css } from 'styled-components';
import COLORS from '@styles/colors/themes/beam/beam2';

export const Container = styled.div`
  height: 100%;
  display: grid;
  grid-template-rows: auto auto auto 1fr;
  position: relative;
`;

export const Content = styled.div`
  overflow: auto;
  overflow-y: overlay;
  overflow-x: hidden;
  height: 100%;
  padding-bottom: 10px;

  ::-webkit-scrollbar {
    width: 24px;
    height: 18px;
    position: fixed;
  }
  ::-webkit-scrollbar-thumb {
    height: 6px;
    border: 8px solid rgba(0, 0, 0, 0);
    background-clip: padding-box;
    border-radius: 12px;
    background-color: rgba(0, 0, 0, 0.15);
  }
  ::-webkit-scrollbar-button {
    width: 0;
    height: 0;
    display: none;
  }
  ::-webkit-scrollbar-corner {
    background-color: transparent;
  }
`;

export const Header = styled.div`
  width: 100%;
  padding: 36px 40px 24px 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const TitleContainer = styled.div`
  color: ${COLORS.neutral.n901};
  font-size: 24px;
  font-weight: 600;
`;

export const Title = styled.div`
  color: ${COLORS.neutral.n901};
  font-size: 24px;
  font-weight: 600;
`;

export const Description = styled.div`
  color: ${COLORS.neutral.n800};
  font-size: 12px;
  font-weight: 400;
`;

export const Buttons = styled.div`
  display: flex;
  align-items: center;
`;

export const FilterButton = styled.button`
  border-radius: 8px;
  padding: 0 10px;
  display: flex;
  align-items: center;
  background: #fff;
  border: 1px solid #d8dae5;
  height: 36px;
  outline: 0;
  margin-right: 8px;
`;

export const AddButton = styled.button`
  border-radius: 8px;
  padding: 0 16px;
  display: flex;
  align-items: center;
  background: #fff;
  border: 1px solid #d8dae5;
  height: 36px;
  outline: 0;
`;

export const AddButtonText = styled.div`
  margin-left: 10px;
  color: #474d66;
  font-size: 14px;
`;

export const Spacing = styled.div`
  margin: 0 40px;
`;

interface IItem {
  disabled?: boolean;
}

export const Item = styled.div<IItem>`
  margin: 0 40px;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
`;

export const ItemContainer = styled.div`
  & + & {
    margin-top: 12px;
  }
`;

export const Divider = styled.div`
  width: 100%;
  background: #e6e8f0;
  height: 1px;
  margin-bottom: 8px;
`;

export const Heading = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 4fr 2fr 2fr 2fr 2fr 2fr 2fr 2fr 3fr 2fr 40px;
  grid-gap: 16px;
  padding: 12px;
`;

export const Direction = styled.div`
  margin-left: 8px;
  height: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ColumnContainer = styled.div`
  display: flex;
  align-items: center;
  user-select: none;
`;

export const ColumnName = styled.div`
  font-size: 12px;
  color: ${COLORS.neutral.n600};
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
`;

export const Line = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 40px;
  grid-gap: 16px;
  padding: 0 12px;
  background: #f5f7fb;
  border-radius: 8px;

  & + & {
    margin-top: 12px;
  }
`;

export const LineClick = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 4fr 2fr 2fr 2fr 2fr 2fr 2fr 2fr 3fr 2fr;
  grid-gap: 16px;
  height: 46px;

  & + & {
    margin-top: 12px;
  }
`;

export const LineProgress = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 40px;
  grid-gap: 16px;
  padding: 0 12px;
  background: #f5f7fb;
  border-radius: 8px;
`;

export const MenuContainer = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
`;

interface ILineText {
  limitOfLines?: number;
}

export const LineText = styled.div<ILineText>`
  color: ${COLORS.neutral.n800};
  font-size: 14px;

  ${({ limitOfLines }) =>
    !!limitOfLines &&
    css`
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: ${limitOfLines}; /* number of lines to show */
      line-clamp: ${limitOfLines};
      -webkit-box-orient: vertical;
    `};

  &[data-hover='true'] {
    cursor: pointer;
  }
`;

export const LineTextContainer = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  overflow: hidden;
  gap: 8px;

  span {
    display: inline-flex;
  }
`;

export const NoText = styled.div`
  color: ${COLORS.neutral.n500};
  font-size: 14px;
  font-style: italic;
  height: 100%;
  display: flex;
  align-items: center;
`;

export const InputContainer = styled.div`
  margin-right: 8px;
  display: flex;
  align-items: center;
  position: relative;
`;

interface IInputSearch {
  search?: boolean;
  clear?: boolean;
}

export const InputSearch = styled.input<IInputSearch>`
  border-radius: 8px;
  padding-left: 32px;
  padding-right: 16px;
  display: flex;
  align-items: center;
  background: ${COLORS.neutral.n0};
  border: 1px solid ${COLORS.neutral.n400};
  height: 42px;
  color: ${COLORS.neutral.n800};
  font-size: 14px;
  width: 250px;

  &::placeholder {
    color: ${COLORS.neutral.n600};
  }

  ${({ search }) =>
    search &&
    css`
      padding-right: 66px;
    `}
  ${({ clear }) =>
    clear &&
    css`
      padding-right: 36px;
    `}
`;

export const SearchIconContainer = styled.div`
  position: absolute;
  left: 12px;
  transform: translateY(-50%);
  top: 50%;
`;

export const SearchClear = styled.div`
  position: absolute;
  right: 16px;
  cursor: pointer;
`;

export const SearchButton = styled.div`
  position: absolute;
  right: 12px;
  font-size: 12px;
  color: #1371fe;
  cursor: pointer;
`;

export const MenuButton = styled.button`
  display: flex;
  height: 42px;
  width: 42px;
  align-items: center;
  justify-content: center;
  border: 1px solid #d8dae5;
  background-color: transparent;
  border-radius: 8px;
  margin-left: 8px;
`;

export const AddedContainer = styled.div`
  display: flex;
  align-items: center;
`;

export const AddedImg = styled.img`
  height: 24px;
  width: 24px;
  border-radius: 12px;
  margin-right: 8px;
`;

export const OpenImporter = styled.button`
  background: ${COLORS.blue.b401};
  border-radius: 8px;
  border: none;
  color: ${COLORS.neutral.n0};
  height: 42px;
  display: flex;
  align-items: center;
  padding: 0 16px;
  font-weight: 500;
  font-size: 14px;
`;

export const LoadMore = styled.div`
  margin-top: 6px;
  display: flex;
  width: 100%;
  align-items: center;
  justify-items: center;
  justify-content: center;
  align-content: center;
  flex-direction: row;
`;

export const LoadingText = styled.div`
  border-radius: 8px;
  margin-right: 8px;
  border: none;
  font-size: 12px;
  color: #696f8c;
  text-align: center;
`;

export const LoadingImg = styled.img`
  height: 14px;
  width: 14px;
`;

export const Back = styled.div`
  height: 1px;
  width: 100%;
  position: absolute;
  bottom: 0;
  background: #edeff4;
  z-index: 1;
`;

export const ItemTab = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;

  & + & {
    margin-left: 48px;
  }
`;

export const PercentageContainer = styled.div`
  grid-column: span 6;
  padding-right: 16px;
  padding-top: 3px;
  overflow: hidden;
`;

export const GlobalMenuContainer = styled.div`
  height: 42px;
  width: 42px;
  border: 1px solid #d8dae5;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const RequestStatusContainer = styled.div`
  overflow: hidden;
  display: flex;
  align-items: center;
`;

export const ErrorMessageContainer = styled.div`
  overflow: hidden;
  display: flex;
  align-items: center;
  grid-column: span 6;
  padding-right: 16px;
  overflow: hidden;
`;

export const ErrorMessageIcon = styled.div`
  height: 20px;
`;

export const ErrorMessage = styled.div`
  font-size: 12px;
  margin-left: 8px;
`;
